/**
 * O documento canônico de uma tela.
 *
 * Substitui os três formatos que coexistiam no modelo antigo — `BuilderJsonOutput`
 * ({tabs,panels,fields}, com `rows` e `contextFields` enfiados por fora da
 * interface), `WebhookConfiguration` e `TemplateComponent[]` — escolhidos por
 * heurística em `mapToJson()`. Aqui há UM formato de escrita, com versão.
 */
import { ScreenNode } from './node-props';

/**
 * Versão do schema. Toda gravação carrega este número, e a leitura RECUSA o que
 * não o declara — não há detecção de formato nem conversão implícita.
 *
 * É o que impede a próxima geração de formatos paralelos. O modelo antigo tinha
 * três convivendo numa coluna só, escolhidos por heurística no `mapToJson()`;
 * aqui, quem mudar a forma do documento incrementa este número e escreve o
 * caminho de leitura da versão anterior, explicitamente.
 */
export const CURRENT_SCHEMA_VERSION = 1;

export interface ScreenMeta {
  name: string;
  description?: string;
  category?: string;

  /**
   * Incrementa a cada gravação. Comparado com a revisão salva para saber se há
   * alteração pendente, e usado como travamento otimista contra a coluna VERSION
   * — que no modelo antigo era gravada como 1 e nunca lida.
   */
  revision: number;
  updatedAt?: string;
  updatedBy?: string;

  /**
   * Origem, quando a tela foi gerada a partir de um JSON. Guardar a amostra e os
   * ajustes manuais é o que permite "reaplicar com JSON novo" sem o usuário
   * refazer tudo à mão quando o payload da API muda.
   */
  generatedFrom?: {
    /** Amostra usada na geração. */
    sample: unknown;
    /** Ajustes que o usuário fez sobre a sugestão. */
    overrides?: readonly FieldOverride[];
  };
}

/** Ajuste manual sobre um campo sugerido pela geração automática. */
export interface FieldOverride {
  /** Caminho no JSON de origem que identifica o campo. */
  readonly sourcePath: string
  /** O que o usuário mudou em relação à sugestão. */
  readonly changed: Readonly<Record<string, unknown>>;
}

export interface ScreenDocument {
  readonly schemaVersion: number;
  /** Identificador estável do documento. Não confundir com IDTEMPLATE do banco. */
  readonly id?: string;
  readonly meta: ScreenMeta;
  /** Raiz única. Sempre existe, mesmo em tela vazia. */
  readonly root: ScreenNode<'screen'>;
  /**
   * Histórico curto, para restaurar revisão anterior sem tabela nova.
   * Guarda patches inversos, não snapshots — cabe no CLOB. Ver `store/patch`.
   */
  readonly history?: readonly RevisionEntry[];
}

export interface RevisionEntry {
  readonly revision: number;
  readonly at: string;
  readonly by?: string;
  /** Patches que desfazem esta revisão. */
  readonly inverse: readonly unknown[];
}

/** Documento vazio: raiz sem filhos. Ponto de partida de "tela em branco". */
export function emptyDocument(name = ''): ScreenDocument {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    meta: { name, revision: 0 },
    root: {
      id: 'screen',
      type: 'screen',
      props: {},
      layout: { order: 0 },
      children: []
    } as ScreenNode<'screen'>
  };
}
